export class DashboardManager {
    constructor(ChartLibrary) {
        this.ChartLibrary = ChartLibrary;
        this.charts = new Map();
        this.monacoEditors = new Map();
        this.refreshTimers = new Map();
        this.grids = new Map();
        this.editorSessions = new Map();
        this.editorSequence = 0;
        this.gridCellHeight = 56;
        this.gridMargin = 8;
        this.contentWidgetTypes = ['header', 'plaintext', 'html'];
    }

    isContentWidget(type) {
        return this.contentWidgetTypes.includes(String(type || '').toLowerCase());
    }

    isHtmlWidget(type) {
        return String(type || '').toLowerCase() === 'html';
    }

    initialize(root = document) {
        this.initializeStaticGrids(root);
        this.initializeEditors(root);
        const widgets = root.querySelectorAll('[data-dashboard-widget]');
        widgets.forEach((widget) => {
            this.mountWidget(widget);
        });
    }

    initializeStaticGrids(root) {
        if (!window.GridStack || !root || typeof root.querySelectorAll !== 'function') {
            return;
        }

        const grids = root.matches?.('[data-dashboard-static-grid]')
            ? [root]
            : root.querySelectorAll('[data-dashboard-static-grid]');

        grids.forEach((gridElement) => {
            if (this.grids.has(gridElement)) {
                return;
            }

            const grid = window.GridStack.init({
                staticGrid: true,
                column: 12,
                cellHeight: this.gridCellHeight,
                margin: this.gridMargin,
                disableOneColumnMode: false,
                acceptWidgets: true
            }, gridElement);

            this.grids.set(gridElement, grid);
        });
    }

    initializeEditors(root) {
        if (!root || typeof root.querySelectorAll !== 'function') {
            return;
        }

        const dashboards = root.matches?.('[data-dashboard-root]')
            ? [root]
            : root.querySelectorAll('[data-dashboard-root]');

        dashboards.forEach((dashboardRoot) => this.initializeEditor(dashboardRoot));
    }

    initializeEditor(dashboardRoot) {
        if (this.editorSessions.has(dashboardRoot)) {
            return;
        }

        const gridElement = dashboardRoot.querySelector('[data-dashboard-static-grid]');
        const dashboardId = dashboardRoot.dataset.dashboardId || '';
        if (!gridElement || !dashboardId) {
            return;
        }

        const session = {
            id: `dashboard-editor-${++this.editorSequence}`,
            root: dashboardRoot,
            dashboardId,
            gridElement,
            grid: this.grids.get(gridElement) || null,
            widgets: this.extractWidgets(gridElement),
            defaults: null,
            editing: false,
            dragReportId: '',
            editButton: dashboardRoot.querySelector('[data-dashboard-edit]'),
            cancelButton: dashboardRoot.querySelector('[data-dashboard-cancel-edit]'),
            refreshButton: dashboardRoot.querySelector('[data-dashboard-refresh]'),
            sidebar: dashboardRoot.querySelector('[data-dashboard-sidebar]'),
            reportPalette: dashboardRoot.querySelector('[data-dashboard-report-palette]'),
            widgetTypePalette: dashboardRoot.querySelector('[data-dashboard-widget-type-palette]'),
            widgetConfig: dashboardRoot.querySelector('[data-dashboard-widget-config]'),
            editorStatus: dashboardRoot.querySelector('[data-dashboard-editor-status]'),
            emptyState: dashboardRoot.querySelector('[data-dashboard-empty-state]')
        };

        session.grid = session.grid || this.grids.get(gridElement) || null;
        this.bindEditorEvents(session);
        this.updateEditorChrome(session);
        this.editorSessions.set(dashboardRoot, session);
    }

    bindEditorEvents(session) {
        session.editButton?.addEventListener('click', () => {
            if (session.editing) {
                this.saveEditorChanges(session);
            } else {
                this.enterEditMode(session);
            }
        });

        session.cancelButton?.addEventListener('click', () => this.exitEditMode(session, true));
        session.refreshButton?.addEventListener('click', () => this.refreshDashboard(session));

        session.gridElement.addEventListener('dragover', (event) => {
            if (!session.editing || !session.dragReportId) {
                return;
            }
            event.preventDefault();
        });

        session.gridElement.addEventListener('drop', (event) => {
            if (!session.editing) {
                return;
            }

            const reportId = event.dataTransfer?.getData('text/plain') || session.dragReportId;
            if (!reportId) {
                return;
            }

            event.preventDefault();
            const placement = this.placementFromDropEvent(session, event);
            this.addReportWidget(session, reportId, placement);
            session.dragReportId = '';
        });

        session.gridElement.addEventListener('dblclick', (event) => {
            if (!session.editing) {
                return;
            }

            const widgetEl = event.target.closest('[data-dashboard-widget]');
            if (!widgetEl) {
                return;
            }

            const widgetId = widgetEl.dataset.widgetId;
            const widget = session.widgets.find((w) => w.sys_id === widgetId);
            if (!widget) {
                return;
            }

            this.openWidgetConfigModal(session, widget);
        });

        session.widgetTypePalette?.querySelectorAll('[data-add-widget-type]').forEach((button) => {
            button.addEventListener('click', () => {
                if (!session.editing) {
                    return;
                }
                this.addWidgetByType(session, button.dataset.addWidgetType || '', null);
            });
        });
    }

    openWidgetConfigModal(session, widget) {
        const modal = new SystemModal(`widget-config-${widget.sys_id}`, false, this.isHtmlWidget(widget.widget_type) ? 'max-w-4xl' : 'max-w-md');
        modal.setTitle('Configure Widget');
        modal.renderWithContent(this.widgetConfigFormHtml(widget));
        modal.modalFooter.innerHTML = `
            <button type="button" class="btn btn-ghost btn-sm" data-config-cancel>Cancel</button>
            <button type="button" class="btn btn-primary btn-sm" data-config-apply>Apply</button>
        `;

        const form = modal.modalBody.querySelector('form');
        this.initializeHtmlWidgetEditor(modal, widget);
        const readField = (field) => form?.querySelector(`[data-config-field="${field}"]`)?.value ?? '';

        modal.modalFooter.querySelector('[data-config-cancel]')?.addEventListener('click', () => modal.hide());
        modal.modalFooter.querySelector('[data-config-apply]')?.addEventListener('click', () => {
            widget.title = readField('title').trim() || widget.report_title || this.defaultTitleForType(widget.widget_type);
            widget.content = readField('content');
            if (!this.isContentWidget(widget.widget_type)) {
                widget.chart_type = readField('chart_type') || 'bar';
            }
            widget.size = readField('size') || 'standard';
            widget.refresh_seconds = Math.max(0, Number(readField('refresh_seconds') || 0));

            const dims = this.sizeToDimensions(widget.size);
            widget.width_units = dims.w;
            widget.height_units = dims.h;

            this.renderGrid(session);
            this.renderWidgetConfig(session);
            this.setEditorStatus(session, `Updated "${widget.title}". Click Save to persist changes.`);
            modal.hide();
        });
    }

    defaultTitleForType(type) {
        switch (String(type || '').toLowerCase()) {
            case 'header':
                return 'Section Header';
            case 'plaintext':
                return 'Text';
            case 'html':
                return 'HTML';
            default:
                return 'Untitled Widget';
        }
    }

    widgetConfigFormHtml(widget) {
        const sizes = ['compact', 'standard', 'wide', 'full'];
        const chartTypes = ['bar', 'line', 'pie', 'doughnut'];
        const isContent = this.isContentWidget(widget.widget_type);
        const contentLabel = String(widget.widget_type || '').toLowerCase() === 'html'
            ? 'HTML Content'
            : (String(widget.widget_type || '').toLowerCase() === 'header' ? 'Header Text' : 'Text Content');

        const contentField = isContent
            ? `
                <label class="form-control">
                    <span class="label-text text-xs uppercase tracking-wide text-base-content/50">${contentLabel}</span>
                    ${this.isHtmlWidget(widget.widget_type)
                        ? `<input type="hidden" data-config-field="content" value="${this.escape(widget.content || '')}">
                           <div class="mt-1 overflow-hidden rounded-xl border border-base-300">
                                <div class="flex items-center justify-between border-b border-base-300 bg-base-200/70 px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-base-content/50">
                                    <span>HTML Editor</span>
                                    <span data-config-monaco-status>Loading Monaco...</span>
                                </div>
                                <div class="hidden w-full bg-base-100" data-config-monaco-editor data-config-monaco-language="html" style="height: 320px"></div>
                                <textarea class="textarea textarea-bordered h-80 w-full rounded-none border-0 font-mono text-sm" data-config-monaco-fallback>${this.escape(widget.content || '')}</textarea>
                            </div>`
                        : `<textarea class="textarea textarea-bordered mt-1 min-h-32 w-full font-mono text-sm" data-config-field="content">${this.escape(widget.content || '')}</textarea>`}
                </label>`
            : `
                <label class="form-control">
                    <span class="label-text text-xs uppercase tracking-wide text-base-content/50">Chart Type</span>
                    <select class="select select-bordered mt-1 w-full" data-config-field="chart_type">
                        ${chartTypes.map((t) => `<option value="${t}" ${widget.chart_type === t ? 'selected' : ''}>${t}</option>`).join('')}
                    </select>
                </label>`;

        return `
            <form class="space-y-4" onsubmit="event.preventDefault()">
                <label class="form-control">
                    <span class="label-text text-xs uppercase tracking-wide text-base-content/50">Title</span>
                    <input type="text" class="input input-bordered mt-1 w-full" value="${this.escape(widget.title || '')}" data-config-field="title">
                </label>
                ${contentField}
                <label class="form-control">
                    <span class="label-text text-xs uppercase tracking-wide text-base-content/50">Size</span>
                    <select class="select select-bordered mt-1 w-full" data-config-field="size">
                        ${sizes.map((s) => `<option value="${s}" ${widget.size === s ? 'selected' : ''}>${s}</option>`).join('')}
                    </select>
                </label>
                <label class="form-control">
                    <span class="label-text text-xs uppercase tracking-wide text-base-content/50">Refresh Interval (seconds, 0 = off)</span>
                    <input type="number" min="0" class="input input-bordered mt-1 w-full" value="${this.escape(String(widget.refresh_seconds || 0))}" data-config-field="refresh_seconds">
                </label>
            </form>
        `;
    }

    initializeHtmlWidgetEditor(modal, widget) {
        if (!this.isHtmlWidget(widget.widget_type)) {
            return;
        }

        const editorContainer = modal.modalBody.querySelector('[data-config-monaco-editor]');
        const hiddenInput = modal.modalBody.querySelector('[data-config-field="content"]');
        const fallbackTextarea = modal.modalBody.querySelector('[data-config-monaco-fallback]');
        const status = modal.modalBody.querySelector('[data-config-monaco-status]');
        if (!editorContainer || !hiddenInput || !fallbackTextarea) {
            return;
        }

        fallbackTextarea.value = hiddenInput.value || '';
        fallbackTextarea.addEventListener('input', () => {
            hiddenInput.value = fallbackTextarea.value;
        });

        this.mountMonacoEditor(editorContainer, hiddenInput.value || '', 'html', (value) => {
            hiddenInput.value = value;
            fallbackTextarea.value = value;
        }, {
            onReady: () => {
                editorContainer.classList.remove('hidden');
                fallbackTextarea.classList.add('hidden');
                if (status) {
                    status.textContent = 'Monaco';
                }
            },
            onUnavailable: () => {
                editorContainer.classList.add('hidden');
                fallbackTextarea.classList.remove('hidden');
                if (status) {
                    status.textContent = 'Textarea Fallback';
                }
            }
        });
    }

    mountMonacoEditor(container, initialValue, language, onChange, hooks = {}) {
        const { onReady = null, onUnavailable = null } = hooks;
        const attemptEditorInit = (retries = 15, interval = 300) => {
            const monaco = window.monaco;
            if (!monaco) {
                if (retries > 0) {
                    window.setTimeout(() => attemptEditorInit(retries - 1, interval), interval);
                } else if (typeof onUnavailable === 'function') {
                    onUnavailable();
                }
                return;
            }

            const existingEditor = this.monacoEditors.get(container);
            if (existingEditor) {
                existingEditor.dispose();
                this.monacoEditors.delete(container);
            }

            const editor = monaco.editor.create(container, {
                value: initialValue || '',
                language,
                wordWrap: 'on',
                automaticLayout: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                fontFamily: 'Consolas, "Courier New", monospace'
            });

            editor.onDidChangeModelContent(() => {
                onChange(editor.getValue());
            });

            this.monacoEditors.set(container, editor);
            if (typeof onReady === 'function') {
                onReady(editor);
            }
        };

        attemptEditorInit();
    }

    extractWidgets(gridElement) {
        return Array.from(gridElement.querySelectorAll('[data-dashboard-widget]')).map((widget, index) => {
            const gridItem = widget.closest('.grid-stack-item');
            const position = this.widgetPlacementFromElement(gridItem, index);
            return {
                sys_id: widget.dataset.widgetId || crypto.randomUUID(),
                report: widget.dataset.reportId || '',
                title: widget.dataset.title || widget.querySelector('h2')?.textContent?.trim() || '',
                widget_type: widget.dataset.widgetType || 'chart',
                chart_type: widget.dataset.chartType || 'bar',
                refresh_seconds: Number(widget.dataset.refreshSeconds || 0),
                options_json: this.parseEncodedValue(widget.dataset.options || '', '{}'),
                content: this.parseEncodedValue(widget.dataset.content || '', ''),
                size: widget.dataset.size || this.sizeForWidthUnits(position.w),
                position: position.y + 1,
                row_position: position.y + 1,
                column_position: position.x + 1,
                width_units: position.w,
                height_units: position.h,
                is_active: true,
                persisted: widget.dataset.persisted !== 'false',
                report_title: widget.querySelector('h2')?.textContent?.trim() || '',
                table_name: widget.dataset.tableName || '',
                aggregate_type: widget.dataset.aggregateType || '',
                group_by_field: widget.dataset.groupByField || ''
            };
        });
    }

    widgetPlacementFromElement(gridItem, index) {
        if (!gridItem) {
            return { x: 0, y: index * 4, w: 6, h: 4 };
        }

        return {
            x: Number(gridItem.getAttribute('gs-x') || 0),
            y: Number(gridItem.getAttribute('gs-y') || index * 4),
            w: Number(gridItem.getAttribute('gs-w') || 6),
            h: Number(gridItem.getAttribute('gs-h') || 4)
        };
    }

    async enterEditMode(session) {
        if (session.editing) {
            return;
        }

        this.setEditorStatus(session, 'Loading dashboard editor...');

        try {
            const response = await fetch(`/api/dashboards/${session.dashboardId}`, {
                credentials: 'same-origin'
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.error || 'Unable to load dashboard editor');
            }

            session.defaults = payload.defaults || { reports: [] };
            session.widgets = this.normalizeSessionWidgets(payload.widgets || []);
            session.editing = true;

            session.grid = session.grid || this.grids.get(session.gridElement);
            session.grid?.setStatic(false);
            session.grid?.enableMove(true);
            session.grid?.enableResize(true);

            session.root.classList.add('dashboard-editing');
            this.renderReportPalette(session);
            this.renderWidgetConfig(session);
            this.renderGrid(session);
            this.updateEditorChrome(session);
            this.setEditorStatus(session, 'Edit mode is on. Drag widgets to rearrange, configure in the sidebar, then click Save.');
        } catch (error) {
            console.error('Failed to enter dashboard edit mode', error);
            this.setEditorStatus(session, error.message || 'Unable to load dashboard editor');
        }
    }

    exitEditMode(session, restoreFromDom = false) {
        session.editing = false;
        session.dragReportId = '';
        session.grid = session.grid || this.grids.get(session.gridElement);
        session.grid?.setStatic(true);
        session.grid?.enableMove(false);
        session.grid?.enableResize(false);

        session.root.classList.remove('dashboard-editing');
        if (restoreFromDom) {
            session.widgets = this.extractWidgets(session.gridElement);
        }
        this.updateEditorChrome(session);
        this.setEditorStatus(session, '');
    }

    updateEditorChrome(session) {
        if (session.editButton) {
            session.editButton.innerHTML = session.editing
                ? '<i class="bi bi-check2-square"></i> Save'
                : '<i class="bi bi-pencil-square"></i> Edit';
        }

        session.cancelButton?.classList.toggle('hidden', !session.editing);
        session.sidebar?.classList.toggle('hidden', !session.editing);
        session.editorStatus?.classList.toggle('hidden', !session.editorStatus.textContent?.trim());

        session.root.querySelectorAll('.dashboard-report-link').forEach((link) => {
            link.classList.toggle('hidden', session.editing);
        });
    }

    renderReportPalette(session) {
        const palette = session.reportPalette;
        if (!palette) {
            return;
        }

        const reports = Array.isArray(session.defaults?.reports) ? session.defaults.reports : [];
        if (!reports.length) {
            palette.innerHTML = '<div class="rounded-xl border border-dashed border-base-300 px-4 py-6 text-center text-sm text-base-content/55">No saved chart reports are available.</div>';
            return;
        }

        palette.innerHTML = reports.map((report) => `
            <div class="flex items-center justify-between gap-2 rounded-xl border border-base-300 bg-base-50/80 px-3 py-2.5" draggable="true" data-report-id="${this.escape(report.sys_id)}">
                <span class="min-w-0 truncate text-sm font-medium text-base-content">${this.escape(report.title || 'Untitled report')}</span>
                <button type="button" class="btn btn-primary btn-xs shrink-0" data-add-report-id="${this.escape(report.sys_id)}">Add</button>
            </div>
        `).join('');

        palette.querySelectorAll('[data-report-id]').forEach((item) => {
            item.addEventListener('dragstart', (event) => {
                const reportId = item.dataset.reportId || '';
                session.dragReportId = reportId;
                event.dataTransfer?.setData('text/plain', reportId);
            });
            item.addEventListener('dragend', () => {
                session.dragReportId = '';
            });
        });

        palette.querySelectorAll('[data-add-report-id]').forEach((button) => {
            button.addEventListener('click', () => {
                this.addReportWidget(session, button.dataset.addReportId || '', null);
            });
        });
    }

    renderWidgetConfig(session) {
        const container = session.widgetConfig;
        if (!container) {
            return;
        }

        const activeWidgets = session.widgets.filter((w) => w.is_active !== false);
        if (!activeWidgets.length) {
            container.innerHTML = '<div class="rounded-xl border border-dashed border-base-300 px-4 py-6 text-center text-sm text-base-content/55">No widgets on this dashboard yet. Add one from above.</div>';
            return;
        }

        container.innerHTML = activeWidgets.map((widget) => {
            const isContent = this.isContentWidget(widget.widget_type);
            const contentField = isContent
                ? `
                <label class="form-control mt-2">
                    <span class="label-text text-[10px] uppercase tracking-wide text-base-content/45">Content</span>
                    ${this.isHtmlWidget(widget.widget_type)
                        ? `<button type="button" class="btn btn-outline btn-sm mt-1 justify-start font-mono" data-open-widget-editor-id="${this.escape(widget.sys_id)}"><i class="bi bi-code-slash"></i> Open HTML editor</button>`
                        : `<textarea class="textarea textarea-bordered textarea-sm mt-1 min-h-24 w-full font-mono text-xs" data-config-field="content" data-config-id="${this.escape(widget.sys_id)}">${this.escape(widget.content || '')}</textarea>`}
                </label>`
                : '';

            return `
            <div class="rounded-xl border border-base-300 bg-base-50/80 p-3" data-config-widget-id="${this.escape(widget.sys_id)}">
                <div class="flex items-center justify-between gap-2">
                    <span class="truncate text-xs font-semibold uppercase tracking-wide text-base-content/50">${widget.persisted ? 'Saved' : 'New'} &middot; ${this.escape(widget.widget_type || 'chart')}</span>
                    <button type="button" class="btn btn-ghost btn-xs text-error" data-remove-widget-id="${this.escape(widget.sys_id)}">
                        <i class="bi bi-trash"></i>
                    </button>
                </div>
                <label class="form-control mt-2">
                    <span class="label-text text-[10px] uppercase tracking-wide text-base-content/45">Title</span>
                    <input type="text" class="input input-bordered input-sm mt-1 w-full" value="${this.escape(widget.title || '')}" data-config-field="title" data-config-id="${this.escape(widget.sys_id)}">
                </label>
                ${contentField}
                <label class="form-control mt-2">
                    <span class="label-text text-[10px] uppercase tracking-wide text-base-content/45">Size</span>
                    <select class="select select-bordered select-sm mt-1 w-full" data-config-field="size" data-config-id="${this.escape(widget.sys_id)}">
                        ${['compact', 'standard', 'wide', 'full'].map((s) => `<option value="${s}" ${widget.size === s ? 'selected' : ''}>${s}</option>`).join('')}
                    </select>
                </label>
            </div>`;
        }).join('');

        container.querySelectorAll('[data-config-field]').forEach((input) => {
            input.addEventListener('input', () => {
                const id = input.dataset.configId;
                const field = input.dataset.configField;
                const widget = session.widgets.find((w) => w.sys_id === id);
                if (!widget) {
                    return;
                }

                widget[field] = input.value;
                if (field === 'size') {
                    const dims = this.sizeToDimensions(input.value);
                    widget.width_units = dims.w;
                    widget.height_units = dims.h;
                    this.updateWidgetInGrid(session, widget);
                } else if (field === 'content' || field === 'title') {
                    this.renderGrid(session);
                }
            });
            input.addEventListener('change', () => {
                if (input.dataset.configField === 'size') {
                    const id = input.dataset.configId;
                    const widget = session.widgets.find((w) => w.sys_id === id);
                    if (widget) {
                        this.updateWidgetInGrid(session, widget);
                    }
                }
            });
        });

        container.querySelectorAll('[data-remove-widget-id]').forEach((button) => {
            button.addEventListener('click', () => {
                this.removeWidget(session, button.dataset.removeWidgetId || '');
            });
        });

        container.querySelectorAll('[data-open-widget-editor-id]').forEach((button) => {
            button.addEventListener('click', () => {
                const widget = session.widgets.find((entry) => entry.sys_id === (button.dataset.openWidgetEditorId || ''));
                if (widget) {
                    this.openWidgetConfigModal(session, widget);
                }
            });
        });
    }

    updateWidgetInGrid(session, widget) {
        const gridItem = session.gridElement.querySelector(`.grid-stack-item[gs-id="${widget.sys_id}"]`);
        if (gridItem && session.grid) {
            session.grid.update(gridItem, {
                w: Math.max(1, Number(widget.width_units || 6)),
                h: Math.max(1, Number(widget.height_units || 4))
            });
        }
    }

    removeWidget(session, widgetId) {
        session.widgets = session.widgets.filter((w) => w.sys_id !== widgetId);
        this.renderGrid(session);
        this.renderWidgetConfig(session);
    }

    placementFromDropEvent(session, event) {
        const rect = session.gridElement.getBoundingClientRect();
        const relativeX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
        const relativeY = Math.max(0, event.clientY - rect.top);
        const x = Math.max(0, Math.min(11, Math.floor((relativeX / Math.max(rect.width, 1)) * 12)));
        const y = Math.max(0, Math.floor(relativeY / (this.gridCellHeight + this.gridMargin)));
        return { x, y, w: 6, h: 4 };
    }

    addReportWidget(session, reportId, placement) {
        if (!reportId || !session.editing) {
            return;
        }

        const report = (session.defaults?.reports || []).find((entry) => entry.sys_id === reportId);
        if (!report) {
            this.setEditorStatus(session, 'Selected report could not be found.');
            return;
        }

        const fallbackPlacement = placement || this.nextAvailablePlacement(session);
        const widget = {
            sys_id: crypto.randomUUID(),
            report: report.sys_id,
            title: report.title || 'Untitled report',
            report_title: report.title || 'Untitled report',
            widget_type: 'chart',
            chart_type: 'bar',
            refresh_seconds: 0,
            options_json: '{}',
            size: 'standard',
            position: fallbackPlacement.y + 1,
            row_position: fallbackPlacement.y + 1,
            column_position: fallbackPlacement.x + 1,
            width_units: fallbackPlacement.w,
            height_units: fallbackPlacement.h,
            is_active: true,
            persisted: false,
            table_name: '',
            aggregate_type: '',
            group_by_field: ''
        };

        session.widgets.push(widget);
        this.renderGrid(session);
        this.renderWidgetConfig(session);
        this.setEditorStatus(session, `Added "${widget.title}". Configure it in the sidebar, then click Save.`);
    }

    addWidgetByType(session, type, placement) {
        const widgetType = String(type || '').toLowerCase();
        if (!this.isContentWidget(widgetType) || !session.editing) {
            return;
        }

        const fallbackPlacement = placement || this.nextAvailablePlacement(session);
        const defaults = this.contentWidgetDefaults(widgetType, fallbackPlacement);
        const dimensions = widgetType === 'header' ? { w: 12, h: 2 } : { w: 6, h: 4 };
        const widget = {
            sys_id: crypto.randomUUID(),
            report: '',
            title: defaults.title,
            report_title: defaults.title,
            widget_type: widgetType,
            chart_type: 'bar',
            refresh_seconds: 0,
            options_json: '{}',
            content: defaults.content,
            size: defaults.size,
            position: fallbackPlacement.y + 1,
            row_position: fallbackPlacement.y + 1,
            column_position: fallbackPlacement.x + 1,
            width_units: dimensions.w,
            height_units: dimensions.h,
            is_active: true,
            persisted: false,
            table_name: '',
            aggregate_type: '',
            group_by_field: ''
        };

        session.widgets.push(widget);
        this.renderGrid(session);
        this.renderWidgetConfig(session);
        this.setEditorStatus(session, `Added "${widget.title}". Configure it in the sidebar, then click Save.`);
    }

    contentWidgetDefaults(widgetType, placement) {
        switch (widgetType) {
            case 'header':
                return { title: 'Section Header', content: 'Section Header', size: 'full' };
            case 'plaintext':
                return { title: 'Text', content: 'Enter your text here.', size: 'standard' };
            case 'html':
                return { title: 'HTML', content: '<p>Enter your HTML here.</p>', size: 'standard' };
            default:
                return { title: 'Widget', content: '', size: 'standard' };
        }
    }

    nextAvailablePlacement(session) {
        if (!session.widgets.length) {
            return { x: 0, y: 0, w: 6, h: 4 };
        }

        const maxY = session.widgets.reduce((currentMax, widget) => {
            const widgetBottom = Number(widget.row_position || 1) - 1 + Number(widget.height_units || 4);
            return Math.max(currentMax, widgetBottom);
        }, 0);

        return { x: 0, y: maxY, w: 6, h: 4 };
    }

    normalizeSessionWidgets(widgets) {
        return widgets.map((widget, index) => ({
            sys_id: widget.sys_id,
            report: widget.report || '',
            title: widget.title || widget.report_title || 'Untitled Widget',
            report_title: widget.report_title || widget.title || 'Untitled Widget',
            widget_type: widget.widget_type || 'chart',
            chart_type: widget.chart_type || 'bar',
            refresh_seconds: Number(widget.refresh_seconds || 0),
            options_json: widget.options_json || '{}',
            content: widget.content || '',
            size: widget.size || this.sizeForWidthUnits(Number(widget.width_units || 6)),
            position: Number(widget.position || index + 1),
            row_position: Number(widget.row_position || widget.position || index + 1),
            column_position: Number(widget.column_position || 1),
            width_units: Number(widget.width_units || 6),
            height_units: Number(widget.height_units || 4),
            is_active: widget.is_active !== false,
            persisted: true,
            table_name: widget.table_name || '',
            aggregate_type: widget.aggregate_type || '',
            group_by_field: widget.group_by_field || ''
        }));
    }

    renderGrid(session) {
        session.grid = session.grid || this.grids.get(session.gridElement);
        if (!session.grid) {
            return;
        }

        Array.from(session.gridElement.querySelectorAll('[data-dashboard-widget]')).forEach((widget) => this.destroyWidget(widget));
        session.grid.removeAll(true);

        const widgets = session.widgets.filter((widget) => widget.is_active !== false);
        if (!widgets.length) {
            if (session.emptyState) {
                session.emptyState.classList.remove('hidden');
                session.gridElement.appendChild(session.emptyState);
            }
            return;
        }

        session.emptyState?.classList.add('hidden');

        widgets.forEach((widget, index) => {
            session.grid.addWidget({
                id: widget.sys_id,
                x: Math.max(0, Number(widget.column_position || 1) - 1),
                y: Math.max(0, Number(widget.row_position || widget.position || index + 1) - 1),
                w: Math.max(1, Number(widget.width_units || 6)),
                h: Math.max(1, Number(widget.height_units || 4)),
                content: this.widgetMarkup(widget, index, session.editing, session.dashboardId)
            });
        });

        Array.from(session.gridElement.querySelectorAll('[data-dashboard-widget]')).forEach((widget) => {
            this.mountWidget(widget);
        });

        session.grid.off('change');
        session.grid.on('change', (_event, items) => {
            if (!session.editing) {
                return;
            }

            items.forEach((item) => {
                const widget = session.widgets.find((entry) => entry.sys_id === item.id);
                if (!widget) {
                    return;
                }

                widget.column_position = Number(item.x || 0) + 1;
                widget.row_position = Number(item.y || 0) + 1;
                widget.position = Number(item.y || 0) + 1;
                widget.width_units = Number(item.w || widget.width_units || 6);
                widget.height_units = Number(item.h || widget.height_units || 4);
                widget.size = this.sizeForWidthUnits(widget.width_units);
            });
        });
    }

    widgetMarkup(widget, index, editing, dashboardId) {
        const widgetType = String(widget.widget_type || 'chart').toLowerCase();
        const isContent = this.isContentWidget(widgetType);
        const title = this.escape(widget.title || widget.report_title || (isContent ? this.defaultTitleForType(widgetType) : 'Untitled Widget'));
        const persisted = widget.persisted !== false;
        const subtitle = isContent
            ? `${this.escape(widgetType)} widget`
            : (widget.table_name && widget.group_by_field
                ? `${this.escape(widget.aggregate_type || 'count')} grouped by ${this.escape(widget.group_by_field)} from ${this.escape(widget.table_name)}`
                : (persisted ? 'Loading widget data...' : 'Save to load data from the linked report.'));
        const reportLink = widget.report && !editing
            ? `<a href="/nav/record/sys_reports/${this.escape(widget.report)}" class="btn btn-outline btn-xs gap-1 dashboard-report-link"><i class="bi bi-pencil"></i> Edit Report</a>`
            : '';
        const badge = isContent ? this.escape(widgetType) : this.escape(widget.chart_type || 'bar');
        const contentAttr = this.escape(encodeURIComponent(widget.content || ''));

        const body = isContent
            ? this.contentBodyMarkup(widget, widgetType, persisted)
            : `
                <div class="flex min-h-0 flex-1 flex-col p-5">
                    <div class="dashboard-widget-meta mb-4 flex flex-wrap items-center gap-2 text-xs text-base-content/55">
                        <span class="rounded-full bg-base-200 px-3 py-1">${persisted ? 'Loading data' : 'Unsaved widget'}</span>
                    </div>
                    <div class="relative min-h-0 flex-1">
                        ${persisted ? `<canvas class="h-full w-full" aria-label="${title}"></canvas>` : '<div class="flex h-full items-center justify-center text-sm text-base-content/40"><i class="bi bi-cloud-arrow-up mr-2 text-lg"></i>Save to load chart data</div>'}
                        <div class="dashboard-widget-empty absolute inset-0 hidden items-center justify-center rounded-2xl border border-dashed border-base-300 bg-base-100/80 px-6 text-center text-sm text-base-content/60">No data available for this widget.</div>
                        <div class="dashboard-widget-error absolute inset-0 hidden items-center justify-center rounded-2xl border border-rose-200 bg-rose-50/95 px-6 text-center text-sm text-rose-700"></div>
                    </div>
                </div>`;

        return `
            <article class="grid-stack-item-content flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-base-300/70 bg-base-100/92 shadow-[0_18px_40px_rgba(15,23,42,0.10)] backdrop-blur" data-dashboard-widget data-persisted="${persisted ? 'true' : 'false'}" data-widget-id="${this.escape(widget.sys_id)}" data-report-id="${this.escape(widget.report || '')}" data-title="${title}" data-widget-type="${this.escape(widgetType)}" data-endpoint="/api/dashboards/${this.escape(dashboardId)}/widgets/${this.escape(widget.sys_id)}" data-chart-type="${this.escape(widget.chart_type || 'bar')}" data-refresh-seconds="${this.escape(String(widget.refresh_seconds || 0))}" data-options="${this.escape(encodeURIComponent(widget.options_json || '{}'))}" data-content="${contentAttr}" data-size="${this.escape(widget.size || 'standard')}" data-position="${this.escape(String(widget.position || 1))}" data-row-position="${this.escape(String(widget.row_position || 1))}" data-column-position="${this.escape(String(widget.column_position || 1))}" data-width-units="${this.escape(String(widget.width_units || 6))}" data-height-units="${this.escape(String(widget.height_units || 4))}" data-table-name="${this.escape(widget.table_name || '')}" data-aggregate-type="${this.escape(widget.aggregate_type || '')}" data-group-by-field="${this.escape(widget.group_by_field || '')}">
                <div class="dashboard-widget-header ${editing ? 'cursor-move' : ''} flex items-start justify-between gap-3 border-b border-base-300/60 px-5 py-4">
                    <div>
                        <div class="text-[11px] uppercase tracking-[0.24em] text-base-content/45">Widget ${index + 1}</div>
                        <h2 class="mt-1 text-lg font-semibold text-base-content">${title}</h2>
                        <p class="mt-1 text-xs text-base-content/55">${subtitle}</p>
                    </div>
                    <div class="flex flex-col items-end gap-2">
                        <div class="rounded-full border border-base-300/80 bg-base-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-base-content/55">${badge}</div>
                        ${reportLink}
                    </div>
                </div>
                ${body}
            </article>
        `;
    }

    contentBodyMarkup(widget, widgetType, persisted) {
        const raw = this.isHtmlWidget(widgetType)
            ? this.decodeHtmlEntities(widget.content || '')
            : (widget.content || '');
        if (widgetType === 'header') {
            const text = this.escape(raw || widget.title || 'Header');
            return `
                <div class="flex min-h-0 flex-1 flex-col justify-center p-5">
                    <div class="dashboard-widget-content text-2xl font-bold leading-tight text-base-content md:text-3xl">${text}</div>
                </div>`;
        }
        if (widgetType === 'plaintext') {
            const text = this.escape(raw);
            return `
                <div class="flex min-h-0 flex-1 flex-col p-5">
                    <div class="dashboard-widget-content min-h-0 flex-1 overflow-auto whitespace-pre-wrap text-sm leading-6 text-base-content/80">${text}</div>
                </div>`;
        }
        return `
            <div class="flex min-h-0 flex-1 flex-col p-5">
                <div class="dashboard-widget-content min-h-0 flex-1 overflow-auto text-sm leading-6 text-base-content/80">${raw}</div>
            </div>`;
    }

    async saveEditorChanges(session) {
        if (!session.editing) {
            return;
        }

        this.setEditorStatus(session, 'Saving dashboard layout...');
        this.syncWidgetsFromGrid(session);

        try {
            for (const widget of session.widgets) {
                const response = await fetch(`/api/dashboards/${session.dashboardId}/widgets`, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        sys_id: widget.sys_id,
                        report: widget.report,
                        title: widget.title,
                        widget_type: widget.widget_type,
                        content: widget.content || '',
                        size: widget.size,
                        position: widget.position,
                        row_position: widget.row_position,
                        column_position: widget.column_position,
                        width_units: widget.width_units,
                        height_units: widget.height_units,
                        refresh_seconds: widget.refresh_seconds,
                        is_active: widget.is_active
                    })
                });

                const payload = await response.json();
                if (!response.ok) {
                    throw new Error(payload.error || 'Unable to save dashboard widget');
                }

                if (payload.widget) {
                    Object.assign(widget, this.normalizeSessionWidgets([payload.widget])[0]);
                }
            }

            this.exitEditMode(session, false);
            this.renderGrid(session);
            this.setEditorStatus(session, 'Dashboard saved.');
            window.setTimeout(() => this.refreshDashboard(session), 50);
        } catch (error) {
            console.error('Failed to save dashboard changes', error);
            this.setEditorStatus(session, error.message || 'Unable to save dashboard changes');
        }
    }

    syncWidgetsFromGrid(session) {
        session.gridElement.querySelectorAll('.grid-stack-item').forEach((item) => {
            const widgetElement = item.querySelector('[data-dashboard-widget]');
            const widgetId = widgetElement?.dataset?.widgetId;
            const widget = session.widgets.find((entry) => entry.sys_id === widgetId);
            if (!widget) {
                return;
            }

            widget.column_position = Number(item.getAttribute('gs-x') || 0) + 1;
            widget.row_position = Number(item.getAttribute('gs-y') || 0) + 1;
            widget.position = Number(item.getAttribute('gs-y') || 0) + 1;
            widget.width_units = Number(item.getAttribute('gs-w') || widget.width_units || 6);
            widget.height_units = Number(item.getAttribute('gs-h') || widget.height_units || 4);
            widget.size = this.sizeForWidthUnits(widget.width_units);
        });
    }

    refreshDashboard(session) {
        Array.from(session.gridElement.querySelectorAll('[data-dashboard-widget][data-persisted="true"]')).forEach((widget) => {
            this.loadWidget(widget);
        });
    }

    destroyWithin(root) {
        if (!root || typeof root.querySelectorAll !== 'function') {
            return;
        }

        const dashboardRoots = root.matches?.('[data-dashboard-root]')
            ? [root]
            : root.querySelectorAll('[data-dashboard-root]');

        dashboardRoots.forEach((dashboardRoot) => {
            this.editorSessions.delete(dashboardRoot);
        });

        const grids = root.matches?.('[data-dashboard-static-grid]')
            ? [root]
            : root.querySelectorAll('[data-dashboard-static-grid]');

        grids.forEach((gridElement) => {
            const grid = this.grids.get(gridElement);
            if (grid) {
                grid.destroy(false);
                this.grids.delete(gridElement);
            }
        });

        const widgets = root.matches?.('[data-dashboard-widget]')
            ? [root]
            : root.querySelectorAll('[data-dashboard-widget]');

        widgets.forEach((widget) => this.destroyWidget(widget));
    }

    mountWidget(widget) {
        const widgetId = widget.dataset.widgetId;
        if (!widgetId) {
            return;
        }

        if (this.isContentWidget(widget.dataset.widgetType)) {
            this.hydrateContentWidget(widget);
            return;
        }

        if (widget.dataset.persisted === 'false') {
            return;
        }

        this.clearTimer(widgetId);
        this.loadWidget(widget);

        const refreshSeconds = Number(widget.dataset.refreshSeconds || 0);
        if (refreshSeconds > 0) {
            const timerId = window.setInterval(() => {
                if (document.body.contains(widget)) {
                    this.loadWidget(widget);
                } else {
                    this.destroyWidget(widget);
                }
            }, refreshSeconds * 1000);
            this.refreshTimers.set(widgetId, timerId);
        }
    }

    hydrateContentWidget(widget) {
        const widgetType = String(widget.dataset.widgetType || '').toLowerCase();
        const container = widget.querySelector('.dashboard-widget-content');
        if (!container) {
            return;
        }

        const rawContent = this.parseEncodedValue(widget.dataset.content || '', '');
        if (widgetType === 'html') {
            container.innerHTML = this.decodeHtmlEntities(rawContent);
            return;
        }

        if (widgetType === 'header') {
            container.textContent = rawContent || widget.dataset.title || 'Header';
            return;
        }

        container.textContent = rawContent;
    }

    async loadWidget(widget) {
        const endpoint = widget.dataset.endpoint;
        const widgetId = widget.dataset.widgetId;
        if (!endpoint || !widgetId) {
            return;
        }

        if (this.isContentWidget(widget.dataset.widgetType)) {
            return;
        }

        if (widget.dataset.persisted === 'false') {
            return;
        }

        this.setMeta(widget, 'Refreshing widget');
        this.toggleState(widget, { empty: false, error: false });

        try {
            const response = await fetch(endpoint, {
                headers: {
                    Accept: 'application/json'
                },
                credentials: 'same-origin'
            });

            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.error || 'Unable to load widget data');
            }

            const labels = Array.isArray(payload?.data?.labels) ? payload.data.labels : [];
            const datasets = Array.isArray(payload?.data?.datasets) ? payload.data.datasets : [];

            if (labels.length === 0 || datasets.length === 0) {
                this.destroyChart(widgetId);
                this.toggleState(widget, { empty: true, error: false });
                this.setMeta(widget, payload?.meta?.summaryText || 'No data returned for this widget');
                return;
            }

            this.renderChart(widget, payload);
            this.toggleState(widget, { empty: false, error: false });
            this.setMeta(widget, payload?.meta?.summaryText || 'Updated');
        } catch (error) {
            console.error('Dashboard widget load failed', error);
            this.destroyChart(widgetId);
            this.toggleState(widget, { empty: false, error: true, errorText: error.message || 'Unable to load widget data' });
            this.setMeta(widget, 'Unable to refresh widget');
        }
    }

    renderChart(widget, payload) {
        const widgetId = widget.dataset.widgetId;
        const canvas = widget.querySelector('canvas');
        if (!canvas || !widgetId) {
            return;
        }

        this.destroyChart(widgetId);

        const decodedOptions = this.parseOptions(widget.dataset.options || '');
        const payloadOptions = payload.options && typeof payload.options === 'object' ? payload.options : {};
        const chartType = payload.chartType || widget.dataset.chartType || 'bar';
        const drilldownUrls = Array.isArray(payload?.meta?.drilldownUrls) ? payload.meta.drilldownUrls : [];
        const mergedOptions = this.deepMerge(
            this.buildOptions(chartType, decodedOptions, payloadOptions),
            {
                onClick: (_event, elements) => {
                    const index = elements?.[0]?.index;
                    const targetUrl = Number.isInteger(index) ? drilldownUrls[index] : '';
                    if (targetUrl) {
                        window.location.href = targetUrl;
                    }
                }
            }
        );

        const chart = new this.ChartLibrary(canvas, {
            type: chartType,
            data: payload.data,
            options: mergedOptions
        });

        canvas.style.cursor = drilldownUrls.length ? 'pointer' : 'default';
        this.charts.set(widgetId, chart);
    }

    buildOptions(chartType, widgetOptions, payloadOptions) {
        const defaultOptions = {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 450,
                easing: 'easeOutQuart'
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        boxWidth: 12,
                        usePointStyle: true,
                        pointStyle: 'circle'
                    }
                },
                tooltip: {
                    padding: 12,
                    cornerRadius: 12,
                    backgroundColor: 'rgba(15, 23, 42, 0.92)',
                    titleColor: '#f8fafc',
                    bodyColor: '#e2e8f0'
                }
            },
            scales: chartType === 'pie' || chartType === 'doughnut'
                ? {}
                : {
                    x: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            color: '#64748b'
                        }
                    },
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(148, 163, 184, 0.18)'
                        },
                        ticks: {
                            color: '#64748b'
                        }
                    }
                }
        };

        return this.deepMerge(defaultOptions, widgetOptions, payloadOptions);
    }

    parseOptions(raw) {
        if (!raw) {
            return {};
        }

        try {
            return JSON.parse(decodeURIComponent(raw));
        } catch (error) {
            console.error('Failed to parse dashboard widget options', error);
            return {};
        }
    }

    parseEncodedValue(raw, fallback = '') {
        if (!raw) {
            return fallback;
        }

        try {
            return decodeURIComponent(raw);
        } catch (_error) {
            return raw;
        }
    }

    decodeHtmlEntities(value) {
        const source = String(value || '');
        if (!source || typeof document === 'undefined') {
            return source;
        }

        const textarea = document.createElement('textarea');
        textarea.innerHTML = source;
        return textarea.value;
    }

    setMeta(widget, text) {
        const meta = widget.querySelector('.dashboard-widget-meta');
        if (!meta) {
            return;
        }

        meta.innerHTML = '';
        const badge = document.createElement('span');
        badge.className = 'rounded-full bg-base-200 px-3 py-1';
        badge.textContent = text;
        meta.appendChild(badge);

        const updated = document.createElement('span');
        updated.className = 'rounded-full border border-base-300 px-3 py-1';
        updated.textContent = `Updated ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
        meta.appendChild(updated);
    }

    setEditorStatus(session, text) {
        if (!session.editorStatus) {
            return;
        }

        session.editorStatus.textContent = text || '';
        session.editorStatus.classList.toggle('hidden', !text);
    }

    toggleState(widget, { empty = false, error = false, errorText = '' }) {
        const emptyState = widget.querySelector('.dashboard-widget-empty');
        const errorState = widget.querySelector('.dashboard-widget-error');

        if (emptyState) {
            emptyState.classList.toggle('hidden', !empty);
            emptyState.classList.toggle('flex', empty);
        }

        if (errorState) {
            errorState.textContent = errorText;
            errorState.classList.toggle('hidden', !error);
            errorState.classList.toggle('flex', error);
        }
    }

    destroyWidget(widget) {
        const widgetId = widget?.dataset?.widgetId;
        if (!widgetId) {
            return;
        }

        this.clearTimer(widgetId);
        this.destroyChart(widgetId);
    }

    destroyChart(widgetId) {
        const chart = this.charts.get(widgetId);
        if (chart) {
            chart.destroy();
            this.charts.delete(widgetId);
        }
    }

    clearTimer(widgetId) {
        const timerId = this.refreshTimers.get(widgetId);
        if (timerId) {
            window.clearInterval(timerId);
            this.refreshTimers.delete(widgetId);
        }
    }

    sizeForWidthUnits(width) {
        if (width >= 12) {
            return 'full';
        }
        if (width >= 8) {
            return 'wide';
        }
        if (width <= 3) {
            return 'compact';
        }
        return 'standard';
    }

    sizeToDimensions(size) {
        switch (size) {
            case 'compact':
                return { w: 3, h: 4 };
            case 'wide':
                return { w: 8, h: 4 };
            case 'full':
                return { w: 12, h: 4 };
            default:
                return { w: 6, h: 4 };
        }
    }

    deepMerge(...sources) {
        const result = {};

        sources.forEach((source) => {
            if (!source || typeof source !== 'object' || Array.isArray(source)) {
                return;
            }

            Object.keys(source).forEach((key) => {
                const existingValue = result[key];
                const nextValue = source[key];

                if (this.isPlainObject(existingValue) && this.isPlainObject(nextValue)) {
                    result[key] = this.deepMerge(existingValue, nextValue);
                } else {
                    result[key] = nextValue;
                }
            });
        });

        return result;
    }

    isPlainObject(value) {
        return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
    }

    escape(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}
